/**
 * AuthService - Production-grade authentication service
 * 
 * Responsibilities:
 * - User authentication (sign in, sign up, sign out)
 * - User profile management
 * - Account creation and linking
 * - Authentication state management
 * 
 * This service focuses ONLY on authentication operations.
 * For HTTP requests, use AuthenticatedClient.
 * For token management, use TokenManager.
 */

import { TokenManager } from './tokenManager';
import { AuthClient, SupabaseClient } from './authenticatedClient';
import { supabase } from './supabase';
import type { User } from '@supabase/supabase-js';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  is_authenticated: boolean;
  auth_user_id: string | null;
  age: number | null;
  country: string | null;
  gender: string | null;
  occupation: string | null;
  education_level: string | null;
  relationship_status: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthResult {
  success: boolean;
  user?: User;
  profile?: UserProfile;
  error?: string;
}

export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signInWithEmail(email: string, password: string): Promise<AuthResult> {
    console.log('🔑 AuthService: Starting sign in for:', email);

    try {
      // Authenticate with Supabase
      const authResponse = await AuthClient.signIn(email, password);

      if (!authResponse.data || authResponse.error) {
        console.error('❌ AuthService: Authentication failed:', {
          error: authResponse.error,
          status: authResponse.status,
          fullResponse: authResponse
        });
        return {
          success: false,
          error: authResponse.error || 'Authentication failed'
        };
      }

      if (!authResponse.data) {
        console.error('❌ AuthService: No data in authentication response');
        return { 
          success: false, 
          error: 'No authentication data received' 
        };
      }

      const { user, access_token, refresh_token, expires_in } = authResponse.data as { user: User; access_token: string; refresh_token: string; expires_in: number };

      if (!user || !access_token || !refresh_token) {
        console.error('❌ AuthService: Invalid authentication response structure');
        return { 
          success: false, 
          error: 'Invalid authentication response' 
        };
      }

      // Store tokens securely
      TokenManager.storeTokens({
        access_token,
        refresh_token,
        expires_in,
        user: {
          id: user.id,
          email: user.email,
          ...(user as unknown as Record<string, unknown>)
        }
      });

      // CRITICAL: Persist session in Supabase client for page reload survival
      // Without this, tokens are in TokenManager but Supabase client doesn't know about the session
      // This ensures ct-auth localStorage is populated so getSession() works on refresh
      const { error: sessionError } = await supabase.auth.setSession({
        access_token,
        refresh_token
      });

      if (sessionError) {
        console.error('❌ AuthService: Failed to persist Supabase session:', sessionError);
        // Continue anyway - tokens are in TokenManager
        // UserContext will handle auth state via REST API
        console.warn('⚠️ AuthService: Session persistence failed but tokens stored - proceeding');
      } else {
        console.log('✅ AuthService: Session persisted in Supabase client (ct-auth storage)');
      }

      console.log('✅ AuthService: Authentication successful, user ID:', user.id);

      // Get user profile
      const profile = await this.getUserProfile(user.id);

      if (!profile) {
        console.log('🔧 AuthService: No profile found, creating one...');
        const createdProfile = await this.createProfileForAuthUser(user);
        
        if (!createdProfile) {
          console.error('❌ AuthService: Failed to create user profile');
          return { 
            success: false, 
            error: 'Failed to create user profile' 
          };
        }

        return { 
          success: true, 
          user, 
          profile: createdProfile 
        };
      }

      console.log('🎯 AuthService: Sign in complete');
      return { 
        success: true, 
        user, 
        profile 
      };

    } catch (error) {
      console.error('💥 AuthService: Unexpected error during sign in:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unexpected error occurred' 
      };
    }
  }

  /**
   * Create new account with email and password
   */
  static async createAccountDirect(
    email: string, 
    password: string, 
    firstName: string = 'User'
  ): Promise<AuthResult> {
    console.log('🔐 AuthService: Starting account creation for:', email);

    try {
      // Create account with Supabase
      const signUpResponse = await AuthClient.signUp(email, password, {
        first_name: firstName
      });

      if (!signUpResponse.data || signUpResponse.error) {
        console.error('❌ AuthService: Account creation failed:', signUpResponse.error);
        
        // Handle rate limiting specifically
        if (signUpResponse.error?.includes('429') || signUpResponse.error?.includes('Too Many Requests')) {
          return { 
            success: false, 
            error: 'Too many signup attempts. Please wait a moment and try again, or try signing in if you already have an account.' 
          };
        }
        
        return { 
          success: false, 
          error: signUpResponse.error || 'Account creation failed' 
        };
      }

      if (!signUpResponse.data) {
        console.error('❌ AuthService: No data in signup response');
        return { 
          success: false, 
          error: 'No signup data received' 
        };
      }

      const { user, access_token, refresh_token } = signUpResponse.data as {
        user: User;
        access_token?: string;
        refresh_token?: string;
      };

      if (!user) {
        console.error('❌ AuthService: No user returned from account creation');
        return {
          success: false,
          error: 'Failed to create user account'
        };
      }

      console.log('✅ AuthService: Account created successfully, user ID:', user.id);

      // CRITICAL: Persist session in Supabase client if tokens were returned
      // This ensures new accounts stay signed in after page reload
      if (access_token && refresh_token) {
        console.log('🔧 AuthService: Persisting session for new account...');

        const { error: sessionError } = await supabase.auth.setSession({
          access_token,
          refresh_token
        });

        if (sessionError) {
          console.error('❌ AuthService: Failed to persist session for new account:', sessionError);
          console.warn('⚠️ AuthService: New account may not persist across page reloads');
        } else {
          console.log('✅ AuthService: New account session persisted in Supabase client');

          // Also store in TokenManager for REST API consistency
          TokenManager.storeTokens({
            access_token,
            refresh_token,
            expires_in: 3600, // Default 1 hour
            user: {
              id: user.id,
              email: user.email
            }
          });
          console.log('✅ AuthService: Tokens stored in TokenManager for new account');
        }
      } else {
        console.warn('⚠️ AuthService: No tokens returned from signup - session may require email confirmation');
      }

      // Create temporary profile for immediate use
      // The database trigger will create the actual profile
      const tempProfile: UserProfile = {
        id: crypto.randomUUID(),
        email: user.email!,
        first_name: firstName,
        is_authenticated: true,
        auth_user_id: user.id,
        age: null,
        country: null,
        gender: null,
        occupation: null,
        education_level: null,
        relationship_status: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('🎯 AuthService: Account creation complete');
      return { 
        success: true, 
        user, 
        profile: tempProfile 
      };

    } catch (error) {
      console.error('💥 AuthService: Unexpected error during account creation:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unexpected error occurred' 
      };
    }
  }

  /**
   * Create account from existing anonymous profile
   */
  static async createAccountFromAnonymous(
    profileId: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; user?: User; error?: string }> {
    console.log('🔗 AuthService: Linking anonymous profile to new account:', { profileId, email });

    try {
      // First create the auth account
      const createResult = await this.createAccountDirect(email, password);
      
      if (!createResult.success || !createResult.user) {
        return { 
          success: false, 
          error: createResult.error || 'Failed to create account' 
        };
      }

      // Update the existing anonymous profile to link it to the auth user
      const updateResponse = await SupabaseClient.update(
        'user_profiles',
        {
          auth_user_id: createResult.user.id,
          is_authenticated: true,
          email: email
        },
        { id: profileId }
      );

      if (updateResponse.error) {
        console.error('❌ AuthService: Failed to link profile to account:', updateResponse.error);
        
        // TODO: Ideally we should clean up the created auth user here
        // For now, just return the error
        return { 
          success: false, 
          error: 'Failed to link account to profile' 
        };
      }

      console.log('✅ AuthService: Anonymous profile successfully linked to account');
      return { 
        success: true, 
        user: createResult.user 
      };

    } catch (error) {
      console.error('💥 AuthService: Unexpected error linking anonymous profile:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unexpected error occurred' 
      };
    }
  }

  /**
   * Sign out user and revoke tokens
   */
  static async signOut(): Promise<{ success: boolean; error?: string }> {
    console.log('🚪 AuthService: Starting sign out...');

    try {
      // 1. Clear Supabase's persisted session (CRITICAL FIX for auto-login on refresh)
      const { error: supabaseError } = await supabase.auth.signOut();
      if (supabaseError) {
        console.error('⚠️ AuthService: Supabase sign out error:', supabaseError);
      } else {
        console.log('✅ AuthService: Supabase session cleared');
      }

      // 2. Revoke tokens on server and clear TokenManager
      const revokeSuccess = await TokenManager.revokeTokens();

      if (revokeSuccess) {
        console.log('✅ AuthService: Token revocation successful');
      } else {
        console.warn('⚠️ AuthService: Server token revocation failed, but local tokens cleared');
      }

      return { success: true };

    } catch (error) {
      console.error('💥 AuthService: Error during sign out:', error);

      // Even if there's an error, clear local tokens
      TokenManager.clearTokens();

      // Also try to clear Supabase session as fallback
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.error('💥 AuthService: Failed to clear Supabase session:', e);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Sign out error occurred'
      };
    }
  }

  /**
   * Get current authenticated user
   */
  static async getCurrentUser(): Promise<User | null> {
    // Check if we have valid tokens first
    if (!TokenManager.isAuthenticated()) {
      return null;
    }

    try {
      const userResponse = await AuthClient.getUser();
      
      if (userResponse.error || !userResponse.data) {
        console.warn('⚠️ AuthService: Failed to get current user:', userResponse.error);
        return null;
      }

      return userResponse.data as User;

    } catch (error) {
      console.error('💥 AuthService: Error getting current user:', error);
      return null;
    }
  }

  /**
   * Get user profile by auth user ID
   */
  static async getUserProfile(authUserId: string): Promise<UserProfile | null> {
    try {
      const profileResponse = await SupabaseClient.select<UserProfile>(
        'user_profiles',
        '*',
        { auth_user_id: authUserId }
      );

      if (profileResponse.error || !profileResponse.data?.length) {
        console.log('ℹ️ AuthService: No profile found for user:', authUserId);
        return null;
      }

      return profileResponse.data[0];

    } catch (error) {
      console.error('💥 AuthService: Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Create profile for existing auth user (internal helper)
   */
  private static async createProfileForAuthUser(user: User): Promise<UserProfile | null> {
    const firstName = user.user_metadata?.first_name || 'User';
    
    try {
      const createResponse = await SupabaseClient.insert<UserProfile>(
        'user_profiles',
        {
          id: crypto.randomUUID(),
          email: user.email!,
          first_name: firstName,
          is_authenticated: true,
          auth_user_id: user.id
        }
      );

      if (createResponse.error || !createResponse.data) {
        console.error('❌ AuthService: Failed to create profile:', createResponse.error);
        return null;
      }

      // Return first item if array, or the item itself
      const profile = Array.isArray(createResponse.data) 
        ? createResponse.data[0] 
        : createResponse.data;

      console.log('✅ AuthService: Profile created successfully');
      return profile;

    } catch (error) {
      console.error('💥 AuthService: Error creating profile:', error);
      return null;
    }
  }

  /**
   * Get user's test sessions
   */
  static async getUserTestSessions(profileId: string) {
    try {
      const sessionsResponse = await SupabaseClient.select(
        'test_sessions',
        '*,test_results(*)',
        { user_id: profileId }
      );

      if (sessionsResponse.error) {
        console.error('💥 AuthService: Error getting test sessions:', sessionsResponse.error);
        return [];
      }

      return sessionsResponse.data || [];

    } catch (error) {
      console.error('💥 AuthService: Error getting test sessions:', error);
      return [];
    }
  }

  /**
   * Refresh user profile data
   */
  static async refreshProfile(authUserId: string): Promise<UserProfile | null> {
    console.log('🔄 AuthService: Refreshing profile for user:', authUserId);
    return this.getUserProfile(authUserId);
  }

  /**
   * Check if user is authenticated (quick local check)
   */
  static isAuthenticated(): boolean {
    return TokenManager.isAuthenticated();
  }

  /**
   * Get current user ID from stored tokens (no network call)
   */
  static getCurrentUserId(): string | null {
    return TokenManager.getCurrentUserId();
  }
}